import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Form, InputGroup, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { get_all_team_users, update_team_user_field } from '../../../utils/services';
import './Admin_panel.css';

const PERMISSION_FIELDS = [
  'devices_management',
  'logistics',
  'project_management',
  'talent_metrics',
  'user_admin',
  'accountant',
  'crm',
  'helpdesk',
  'company_dashs',
  'pin_management',
  'workshops',
  'tomi8api',
];

const IDENTITY_FIELDS = [
  'id',
  'usr_name',
  'email_id',
  'type',
  'status',
  'invited_by',
  'member_trello_id',
  'last_login',
];

const NON_EDITABLE_FIELDS = new Set(['id']);

function matchesSearch(user, term) {
  if (!term) {
    return true;
  }
  const normalized = term.toLowerCase();
  return Object.values(user).some((value) => {
    if (value == null) {
      return false;
    }
    return String(value).toLowerCase().includes(normalized);
  });
}

function cellKey(userId, field) {
  return `${userId}:${field}`;
}

function Admin_panel() {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await get_all_team_users(token);
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (!Array.isArray(result)) {
        setUsers([]);
        setError('No se pudieron cargar los usuarios.');
        return;
      }
      setUsers(result);
    } catch (err) {
      setError('Error al cargar los usuarios.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const extraFields = useMemo(() => {
    if (users.length === 0) {
      return [];
    }
    const known = new Set([...IDENTITY_FIELDS, ...PERMISSION_FIELDS]);
    return Object.keys(users[0]).filter((key) => !known.has(key));
  }, [users]);

  const columns = useMemo(
    () => [...IDENTITY_FIELDS, ...extraFields, ...PERMISSION_FIELDS],
    [extraFields]
  );

  const filteredUsers = useMemo(
    () => users.filter((user) => matchesSearch(user, searchTerm.trim())),
    [users, searchTerm]
  );

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const startEdit = (user, field) => {
    if (NON_EDITABLE_FIELDS.has(field) || savingCell) {
      return;
    }
    setEditingCell({ userId: user.id, field });
    setEditValue(user[field] ?? '');
  };

  const saveEdit = async (user, field) => {
    const currentValue = user[field] ?? '';
    const nextValue = editValue;

    if (String(currentValue) === String(nextValue)) {
      cancelEdit();
      return;
    }

    const savingKey = cellKey(user.id, field);
    setSavingCell(savingKey);

    try {
      const result = await update_team_user_field(token, user.id, field, nextValue);
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (result !== 'OK') {
        setError(`No se pudo actualizar ${field}.`);
        cancelEdit();
        return;
      }

      setUsers((prev) =>
        prev.map((row) =>
          row.id === user.id ? { ...row, [field]: nextValue } : row
        )
      );
      setError('');
      cancelEdit();
    } catch (err) {
      setError(`Error al actualizar ${field}.`);
      cancelEdit();
    } finally {
      setSavingCell(null);
    }
  };

  const renderCell = (user, col) => {
    const isEditable = !NON_EDITABLE_FIELDS.has(col);
    const isEditing =
      editingCell?.userId === user.id && editingCell?.field === col;
    const isSaving = savingCell === cellKey(user.id, col);
    const displayValue = user[col] ?? '';

    if (isEditing) {
      return (
        <input
          className="admin-panel__edit-input"
          type="text"
          value={editValue}
          autoFocus
          disabled={isSaving}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveEdit(user, col)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              saveEdit(user, col);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
          }}
        />
      );
    }

    return (
      <span
        className={isEditable ? 'admin-panel__cell-value admin-panel__cell-value--editable' : 'admin-panel__cell-value'}
        title={isEditable ? 'Doble clic para editar' : undefined}
        onDoubleClick={() => startEdit(user, col)}
      >
        {displayValue}
      </span>
    );
  };

  return (
    <Container fluid className="admin-panel p-4">
      <div className="admin-panel__header">
        <div>
          <h2 className="mb-1">Panel Admin</h2>
          <p className="text-muted mb-0">Usuarios y permisos del equipo. Doble clic en un campo para editarlo.</p>
        </div>
        <InputGroup className="admin-panel__search">
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Buscar por nombre, email, id o cualquier campo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading && <p className="text-muted mt-4">Cargando usuarios...</p>}
      {!loading && error && <p className="text-danger mt-4">{error}</p>}

      {!loading && (
        <>
          <p className="admin-panel__count text-muted">
            {filteredUsers.length} de {users.length} usuarios
          </p>
          <div className="table-responsive admin-panel__table-wrap">
            <Table striped bordered hover size="sm" className="admin-panel__table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col}
                      className={PERMISSION_FIELDS.includes(col) ? 'admin-panel__perm-col' : undefined}
                      title={col}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center text-muted">
                      No se encontraron usuarios.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id ?? user.email_id}>
                      {columns.map((col) => (
                        <td
                          key={col}
                          className={PERMISSION_FIELDS.includes(col) ? 'admin-panel__perm-col' : undefined}
                        >
                          {renderCell(user, col)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </Container>
  );
}

export default Admin_panel;
